/**
 * Push Settings Page Component
 * Allows users to manage notification preferences and test notifications
 *
 * Features:
 * - Enable/disable all notifications
 * - Toggle notification by category
 * - Sound and badge preferences
 * - Test notification button
 * - Device management
 * - Notification status indicator
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import {
  enableNotifications,
  disableNotifications,
  sendTestNotification,
  areNotificationsEnabled,
  getNotificationPermissionStatus,
  getUserDevices,
  requestNotificationPermission,
} from '../services/pushNotificationService';
import { db } from '../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import './PushSettingsPage.css';

interface PushPreferences {
  enabled: boolean;
  sound: boolean;
  badge: boolean;
  categories: {
    transactions: boolean;
    expiryWarnings: boolean;
    adminAlerts: boolean;
    weeklyReports: boolean;
    promotions: boolean;
  };
}

interface Device {
  tokenId: string;
  deviceType: string;
  browser: string;
  os: string;
  createdAt: Date;
  lastUsed: Date;
}

/**
 * PushSettingsPage Component
 */
export const PushSettingsPage: React.FC = () => {
  const [preferences, setPreferences] = useState<PushPreferences | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  // Load preferences
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    loadPreferences();
    loadDevices();
  }, [userId]);

  const loadPreferences = async () => {
    try {
      const prefsDoc = await getDoc(
        doc(db, 'users', userId!, 'pushPreferences', 'settings')
      );

      if (prefsDoc.exists()) {
        setPreferences(prefsDoc.data() as PushPreferences);
      } else {
        // Default preferences
        const defaultPrefs: PushPreferences = {
          enabled: false,
          sound: true,
          badge: true,
          categories: {
            transactions: true,
            expiryWarnings: true,
            adminAlerts: true,
            weeklyReports: false,
            promotions: false,
          },
        };
        setPreferences(defaultPrefs);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      setMessage({ type: 'error', text: 'Failed to load preferences' });
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      const devs = await getUserDevices();
      setDevices(devs);
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };

  const handleEnableToggle = async () => {
    try {
      setSaving(true);

      if (preferences?.enabled) {
        // Disable notifications
        await disableNotifications();
        setPreferences(prev => prev ? { ...prev, enabled: false } : null);
        setMessage({ type: 'success', text: 'Notifications disabled' });
      } else {
        // Enable notifications
        const result = await requestNotificationPermission();
        if (result.status === 'granted' && result.token) {
          setPreferences(prev => prev ? { ...prev, enabled: true } : null);
          setMessage({
            type: 'success',
            text: 'Notifications enabled! You will receive notifications.',
          });
          await loadDevices();
        } else {
          setMessage({
            type: 'error',
            text:
              'Failed to enable notifications. Please check browser permissions.',
          });
        }
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
      setMessage({ type: 'error', text: 'Failed to update notification settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryToggle = async (category: keyof PushPreferences['categories']) => {
    try {
      if (!preferences) return;

      const updated = {
        ...preferences,
        categories: {
          ...preferences.categories,
          [category]: !preferences.categories[category],
        },
      };

      await setDoc(
        doc(db, 'users', userId!, 'pushPreferences', 'settings'),
        updated,
        { merge: true }
      );

      setPreferences(updated);
      setMessage({ type: 'success', text: 'Preferences updated' });
    } catch (error) {
      console.error('Error updating preferences:', error);
      setMessage({ type: 'error', text: 'Failed to update preferences' });
    }
  };

  const handleSoundToggle = async () => {
    try {
      if (!preferences) return;

      const updated = {
        ...preferences,
        sound: !preferences.sound,
      };

      await setDoc(
        doc(db, 'users', userId!, 'pushPreferences', 'settings'),
        updated,
        { merge: true }
      );

      setPreferences(updated);
      setMessage({ type: 'success', text: 'Sound preference updated' });
    } catch (error) {
      console.error('Error updating sound preference:', error);
      setMessage({ type: 'error', text: 'Failed to update sound preference' });
    }
  };

  const handleBadgeToggle = async () => {
    try {
      if (!preferences) return;

      const updated = {
        ...preferences,
        badge: !preferences.badge,
      };

      await setDoc(
        doc(db, 'users', userId!, 'pushPreferences', 'settings'),
        updated,
        { merge: true }
      );

      setPreferences(updated);
      setMessage({ type: 'success', text: 'Badge preference updated' });
    } catch (error) {
      console.error('Error updating badge preference:', error);
      setMessage({ type: 'error', text: 'Failed to update badge preference' });
    }
  };

  const handleSendTest = async () => {
    try {
      setTesting(true);

      if (!areNotificationsEnabled()) {
        setMessage({
          type: 'error',
          text: 'Please enable notifications first',
        });
        setTesting(false);
        return;
      }

      const success = await sendTestNotification();
      if (success) {
        setMessage({
          type: 'success',
          text: 'Test notification sent! Check your browser.',
        });
      } else {
        setMessage({
          type: 'error',
          text: 'Failed to send test notification. Make sure notifications are enabled.',
        });
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      setMessage({ type: 'error', text: 'Failed to send test notification' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="push-settings-page">Loading preferences...</div>;
  }

  if (!preferences) {
    return <div className="push-settings-page">Failed to load preferences</div>;
  }

  return (
    <div className="push-settings-page">
      <div className="settings-container">
        <h1>📬 Notification Settings</h1>

        {message && (
          <div className={`message message-${message.type}`}>
            {message.type === 'success' ? '✓' : '✕'} {message.text}
          </div>
        )}

        {/* Main Enable/Disable */}
        <div className="settings-section">
          <div className="section-header">
            <h2>Push Notifications</h2>
            <div className="status-badge">
              {preferences.enabled ? (
                <span className="status-enabled">● Enabled</span>
              ) : (
                <span className="status-disabled">○ Disabled</span>
              )}
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label htmlFor="enable-toggle">
                <strong>Enable All Notifications</strong>
              </label>
              <p className="setting-description">
                Turn on to receive real-time notifications about transactions, expiry
                warnings, and updates
              </p>
            </div>
            <label className="toggle-switch">
              <input
                id="enable-toggle"
                type="checkbox"
                checked={preferences.enabled}
                onChange={handleEnableToggle}
                disabled={saving}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Notification Categories */}
        {preferences.enabled && (
          <>
            <div className="settings-section">
              <h2>Notification Types</h2>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="transactions-toggle">
                    <strong>✓ Transaction Confirmations</strong>
                  </label>
                  <p className="setting-description">
                    Receive notifications immediately after redeeming or claiming giftcards
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    id="transactions-toggle"
                    type="checkbox"
                    checked={preferences.categories.transactions}
                    onChange={() => handleCategoryToggle('transactions')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="expiry-toggle">
                    <strong>⏰ Expiry Warnings</strong>
                  </label>
                  <p className="setting-description">
                    Get alerted 7 days before your giftcards expire
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    id="expiry-toggle"
                    type="checkbox"
                    checked={preferences.categories.expiryWarnings}
                    onChange={() => handleCategoryToggle('expiryWarnings')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="admin-toggle">
                    <strong>🚨 Admin Alerts</strong>
                  </label>
                  <p className="setting-description">
                    Important alerts about your account or large transactions
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    id="admin-toggle"
                    type="checkbox"
                    checked={preferences.categories.adminAlerts}
                    onChange={() => handleCategoryToggle('adminAlerts')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="reports-toggle">
                    <strong>📊 Weekly Reports</strong>
                  </label>
                  <p className="setting-description">
                    Summary of weekly activity (for admin users only)
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    id="reports-toggle"
                    type="checkbox"
                    checked={preferences.categories.weeklyReports}
                    onChange={() => handleCategoryToggle('weeklyReports')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="promotions-toggle">
                    <strong>🎉 Special Offers</strong>
                  </label>
                  <p className="setting-description">
                    Promotional offers and special announcements
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    id="promotions-toggle"
                    type="checkbox"
                    checked={preferences.categories.promotions}
                    onChange={() => handleCategoryToggle('promotions')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            {/* Sound and Badge Settings */}
            <div className="settings-section">
              <h2>Notification Style</h2>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="sound-toggle">
                    <strong>🔊 Sound</strong>
                  </label>
                  <p className="setting-description">
                    Play a sound when you receive notifications
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    id="sound-toggle"
                    type="checkbox"
                    checked={preferences.sound}
                    onChange={handleSoundToggle}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="badge-toggle">
                    <strong>🔴 Badge Count</strong>
                  </label>
                  <p className="setting-description">
                    Show notification count badge on the app icon
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    id="badge-toggle"
                    type="checkbox"
                    checked={preferences.badge}
                    onChange={handleBadgeToggle}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            {/* Test Notification */}
            <div className="settings-section">
              <h2>Test Notifications</h2>
              <p className="section-description">
                Send yourself a test notification to verify everything is working correctly
              </p>
              <button
                className="test-button"
                onClick={handleSendTest}
                disabled={testing || !preferences.enabled}
              >
                {testing ? 'Sending...' : '🧪 Send Test Notification'}
              </button>
            </div>

            {/* Registered Devices */}
            {devices.length > 0 && (
              <div className="settings-section">
                <h2>Registered Devices</h2>
                <p className="section-description">
                  Notifications will be sent to all these devices:
                </p>

                <div className="devices-list">
                  {devices.map(device => (
                    <div key={device.tokenId} className="device-item">
                      <div className="device-icon">
                        {device.deviceType === 'mobile'
                          ? '📱'
                          : device.deviceType === 'tablet'
                          ? '📱'
                          : '💻'}
                      </div>
                      <div className="device-info">
                        <div className="device-name">
                          {device.browser} on {device.os}
                        </div>
                        <div className="device-meta">
                          Added {formatDate(device.createdAt)} • Last used{' '}
                          {formatDate(device.lastUsed)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Info Box */}
        <div className="info-box">
          <h3>💡 Tips</h3>
          <ul>
            <li>Notifications work best with your browser set to allow notifications</li>
            <li>You can change these settings at any time</li>
            <li>Each device will receive notifications independently</li>
            <li>Notifications are never shared with third parties</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

/**
 * Format date for display
 */
function formatDate(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hours = Math.floor(diff / (60 * 60 * 1000));
  const minutes = Math.floor(diff / (60 * 1000));

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return date.toLocaleDateString();
}

export default PushSettingsPage;
